import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import diagnosticsApi from '@/api/operations/diagnosticsApi'
import { SyncErrorIcon } from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import useResetIdFlowValve from '@/features/config/hooks/hydraulic/flowValve/useResetIdFlowValve'
import useStationErrorLogs from '@/features/config/hooks/station/useStationErrorLogs'
import { NotifyService } from '@/helpers/notify'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { getStationErrorDetailText } from '@/utils/stationErrorDetail'
import { stationErrorLogKey } from '@/utils/stationErrorLog'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { FC, Fragment, useEffect, useState } from 'react'
import {
  DiagnosticRow,
  ErrorStatusCell,
  MUTED_TEXT,
  ReasonText,
  SuggestedActionList,
  groupedCellSx,
  primaryActionSx,
  secondaryActionSx,
} from '../ErrorGuidance'

interface FlowValveTableProps {
  rows: any
  /** Pipe name per flow rule id, to show which pipe a valve sits on. */
  pipeNameByRule?: Record<number, string>
}

interface DiagnosedDetail {
  /** Diagnose found the valve working: the error code is from the past. */
  passed?: boolean
  reason: string
  actions: string[]
  /** Only set for a Diagnose result: what it reported and when. */
  code?: number
  message?: string
  at?: number
}

const LOCAL_KEY_ERROR = 'flowvalve-id-error'

const FlowValveTable: FC<FlowValveTableProps> = ({ rows, pipeNameByRule }) => {
  const { t } = useTranslation()

  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const { mutate: resetById, isSuccess } = useResetIdFlowValve()
  const { data: errorLogs, isInitialLoading: errorLogsLoading } =
    useStationErrorLogs(Number(interfaceId), rows)

  const [arrayReset, setArrayReset] = useState<any>([])
  const [diagnoseLoading, setDiagnoseLoading] = useState<number[]>([])
  const [diagnosed, setDiagnosed] = useState<Record<number, DiagnosedDetail>>(
    {},
  )

  const getKeyReset = localStorage.getItem(LOCAL_KEY_ERROR)

  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_KEY_ERROR)
    if (saved) {
      setArrayReset(JSON.parse(saved))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // errorName is frequently empty on flow valve errors, so fall back to the
  // flowValveError_<code> translation, then to the raw code.
  const getErrorMessage = (row: any): string => {
    const name = row.errorName?.trim()
    if (name && name !== 'None') return name
    if (row.errorCode) {
      const key = `flowValveError_${row.errorCode}`
      const translated = t(key)
      if (translated && translated !== key) return translated
      return String(row.errorCode)
    }
    return '-'
  }

  // Guidance for the recorded error, from the latest io::on / io::off log
  const getLoggedDetail = (row: any) =>
    getStationErrorDetailText(
      t,
      row.errorCode,
      errorLogs?.get(stationErrorLogKey(row.address, row.index, row.errorCode))
        ?.errDetail,
    )

  const handleResetId = (id: any) => {
    if (isViewOnly) return
    if (getKeyReset === null || !getKeyReset?.includes(id)) {
      resetById({
        interfaceId: Number(interfaceId),
        id: id,
      })

      const arrayNew = [...arrayReset, id]
      setArrayReset(arrayNew)

      if (!isSuccess) {
        setTimeout(function () {
          setArrayReset([])
        }, 2000)
      }
    }
  }

  // The decoder diagnostic takes "address-index" ids on the current BE; the
  // newer BE takes the decoder output's GUID (looked up by address + index).
  const runValveDiagnostic = async (row: any) => {
    const run = async (decoderId: string) => {
      const res: any = await diagnosticsApi.runDecoderDiagnostic({
        interfaceId: Number(interfaceId),
        decoderIds: [decoderId],
      })
      return Array.isArray(res?.data) && res.data.length ? res.data[0] : null
    }

    try {
      const item = await run(`${row.address}-${row.index}`)
      if (item) return item
    } catch (error) {
      console.error('Diagnose by address-index failed, retrying by id:', error)
    }

    const { data } = await controllerDataConfigApi.getAll({
      interfaceId: Number(interfaceId),
      PageSize: 0,
    })
    const decoder = (data?.data ?? []).find(
      (item: any) =>
        String(item.address) === String(row.address) &&
        String(item.index) === String(row.index),
    )
    return decoder ? run(decoder.id) : null
  }

  const handleDiagnose = async (row: any) => {
    setDiagnoseLoading((prev) =>
      prev.includes(row.id) ? prev : [...prev, row.id],
    )
    try {
      const item = await runValveDiagnostic(row)
      if (!item) {
        console.error('Diagnose returned no result for flow valve', row)
        NotifyService.error(
          t('errorsGeneralSomethingWentWrongPleaseTryAgainLater'),
        )
        return
      }
      const detail = item.error ?? item.result
      setDiagnosed((prev) => ({
        ...prev,
        [row.id]: {
          passed: item.status ? item.status === 'Normal' : !detail,
          code: detail?.code,
          message: detail?.description ?? '',
          at: Date.now(),
          reason: detail?.reason ?? '',
          actions: String(detail?.action ?? '').split('\n'),
        },
      }))
    } catch (error) {
      console.error('Flow valve diagnose failed:', error)
      NotifyService.error(error)
    } finally {
      setDiagnoseLoading((prev) => prev.filter((x) => x !== row.id))
    }
  }

  const headCellSx = {
    fontSize: '12px',
    fontWeight: 600,
    color: MUTED_TEXT,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap' as const,
  }

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ width: '100%' }} aria-label={t('flowValve')}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, pl: '20px' }}>
                {t('flowValve')}
              </TableCell>
              <TableCell sx={headCellSx}>{t('commonStatusError')}</TableCell>
              <TableCell sx={headCellSx}>{t('diagnosticReason')}</TableCell>
              <TableCell sx={headCellSx}>{t('suggestedAction')}</TableCell>
              <TableCell sx={{ ...headCellSx, textAlign: 'right', pr: '20px' }}>
                {t('interfaceTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& .MuiTableCell-root': { verticalAlign: 'top' } }}>
            {rows.map((row: any, rowIndex: number) => {
              const logged = getLoggedDetail(row)
              const result = diagnosed[row.id]
              const isDiagnosing = diagnoseLoading.includes(row.id)
              const hasDiagnosticRow = isDiagnosing || !!result?.at
              const past = !!result?.passed
              const rowBg = rowIndex % 2 ? '#F9F9F9' : '#FFFFFF'
              const cellSx = {
                py: 2,
                ...(hasDiagnosticRow ? groupedCellSx : {}),
              }
              const pipeName = pipeNameByRule?.[row.flowRuleId]
              const loggedAt = errorLogs?.get(
                stationErrorLogKey(row.address, row.index, row.errorCode),
              )?.timestamp
              return (
                <Fragment key={row.id}>
                  <TableRow sx={{ bgcolor: rowBg }}>
                    <TableCell sx={{ ...cellSx, pl: '20px', width: 150 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                        {row.pipeName || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: MUTED_TEXT,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('formsLabelsDecoder')}{' '}
                        {`${row.address}-${row.index}`}
                      </Typography>
                      {pipeName && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: MUTED_TEXT,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t('formsLabelsPipe')} {pipeName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 190 }}>
                      <ErrorStatusCell
                        code={row.errorCode}
                        message={getErrorMessage(row)}
                        recordedAt={loggedAt}
                        recordedAtLoading={errorLogsLoading}
                        past={past}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ReasonText text={logged?.reason} muted={past} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <SuggestedActionList
                        actions={logged?.actions ?? []}
                        muted={past}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, pr: '20px', width: 150 }}>
                      <Stack
                        spacing={1}
                        alignItems="stretch"
                        sx={{ opacity: isViewOnly ? 0.5 : 1 }}
                      >
                        <LoadingButton
                          variant="primary"
                          loading={isDiagnosing}
                          disabled={isViewOnly}
                          onClick={() => handleDiagnose(row)}
                          sx={primaryActionSx}
                        >
                          {result
                            ? t('errorSummaryDiagnoseAgain')
                            : t('errors.diagnose')}
                        </LoadingButton>
                        <LoadingButton
                          variant="secondary"
                          loading={arrayReset?.includes(row.id)}
                          loadingPosition="start"
                          startIcon={<SyncErrorIcon />}
                          disabled={isViewOnly}
                          onClick={() => handleResetId(row.id)}
                          sx={secondaryActionSx}
                        >
                          {t('errors.acknowledge')}
                        </LoadingButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <DiagnosticRow
                    running={isDiagnosing}
                    rowBg={rowBg}
                    loggedReason={logged?.reason}
                    loggedActions={logged?.actions}
                    recordedCode={row.errorCode}
                    diagnosis={
                      result?.at
                        ? {
                            passed: !!result.passed,
                            code: result.code,
                            message: result.message,
                            reason: result.reason,
                            actions: result.actions,
                            at: result.at,
                          }
                        : undefined
                    }
                  />
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default FlowValveTable
